/** Поведение модального окна, общее для всех диалогов приложения.
 *
 *  Что делает, пока окно открыто:
 *  - переводит фокус внутрь (в `initialFocus`, иначе в первый элемент);
 *  - не выпускает Tab наружу: с последнего элемента — на первый и обратно;
 *  - Escape закрывает (если `dismissable`);
 *  - прячет прокрутку страницы под окном;
 *  - при закрытии возвращает фокус туда, откуда окно открыли.
 *
 *  Диалоги бывают вложенными (подтверждение поверх формы): клавиши
 *  обрабатывает только верхний — иначе нижний утащил бы фокус к себе.
 *  Раньше всё это умело одно окно из двенадцати (создание закупки), у
 *  остальных Tab уходил в меню за окном, а Escape не работал.
 */
import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

const stack: HTMLElement[] = [];

function focusable(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !el.matches(":disabled") && el.tabIndex >= 0 && el.getClientRects().length > 0,
  );
}

export function useDialog(
  ref: RefObject<HTMLElement>,
  {
    open,
    onClose,
    dismissable = true,
    initialFocus,
  }: {
    open: boolean;
    onClose: () => void;
    /** false — Escape не закрывает: окно требует ответа. */
    dismissable?: boolean;
    /** Селектор элемента, который получает фокус при открытии. */
    initialFocus?: string;
  },
) {
  // Обработчик читает актуальный onClose без перезапуска эффекта:
  // страницы часто передают новую стрелку на каждом рендере.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const node = ref.current;
    if (!node) return;

    const opener = document.activeElement as HTMLElement | null;
    stack.push(node);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const frame = requestAnimationFrame(() => {
      if (node.contains(document.activeElement)) return; // страница уже поставила фокус сама
      const target =
        (initialFocus ? node.querySelector<HTMLElement>(initialFocus) : null) ?? focusable(node)[0] ?? node;
      if (target === node && node.tabIndex < 0) node.tabIndex = -1;
      target.focus({ preventScroll: true });
    });

    const keydown = (event: KeyboardEvent) => {
      if (stack[stack.length - 1] !== node || event.defaultPrevented) return;
      if (event.key === "Escape") {
        if (dismissable) {
          event.preventDefault();
          closeRef.current();
        }
        return;
      }
      if (event.key !== "Tab") return;
      const elements = focusable(node);
      if (elements.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      const outside = !node.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", keydown);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", keydown);
      const index = stack.lastIndexOf(node);
      if (index >= 0) stack.splice(index, 1);
      document.body.style.overflow = overflow;
      if (opener?.isConnected && opener !== document.body) opener.focus({ preventScroll: true });
    };
  }, [open, dismissable, initialFocus, ref]);
}
